/*
 * RNNoise-based noise suppression for LiveKit microphone tracks.
 * Заменяет Chrome WebRTC NS (агрессивно режет HF, "плосковатый" voice)
 * на ML-based RNNoise (сохраняет voice quality, удаляет только шум).
 *
 * Apply: micTrack.setProcessor(new RNNoiseProcessor())
 */
import {RnnoiseWorkletNode, loadRnnoise} from '@sapphi-red/web-noise-suppressor';
import rnnoiseWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url';
import rnnoiseSimdWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url';
import rnnoiseWorkletUrl from '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url';
import type {Track, TrackProcessor} from 'livekit-client';

export class RNNoiseProcessor implements TrackProcessor<Track.Kind.Audio> {
	name = 'rnnoise-denoiser';

	private audioContext?: AudioContext;
	private source?: MediaStreamAudioSourceNode;
	private rnnoise?: RnnoiseWorkletNode;
	private destination?: MediaStreamAudioDestinationNode;

	processedTrack?: MediaStreamTrack;

	async init(opts: {track: MediaStreamTrack; audioContext?: AudioContext}): Promise<void> {
		this.audioContext = opts.audioContext ?? new AudioContext({sampleRate: 48000});

		const wasmBinary = await loadRnnoise({url: rnnoiseWasmUrl, simdUrl: rnnoiseSimdWasmUrl});
		await this.audioContext.audioWorklet.addModule(rnnoiseWorkletUrl);

		this.source = this.audioContext.createMediaStreamSource(new MediaStream([opts.track]));
		this.rnnoise = new RnnoiseWorkletNode(this.audioContext, {
			wasmBinary,
			maxChannels: 2,
		});
		this.destination = this.audioContext.createMediaStreamDestination();

		this.source.connect(this.rnnoise).connect(this.destination);
		this.processedTrack = this.destination.stream.getAudioTracks()[0];
	}

	async restart(opts: {track: MediaStreamTrack; audioContext?: AudioContext}): Promise<void> {
		await this.destroy();
		await this.init(opts);
	}

	async destroy(): Promise<void> {
		this.source?.disconnect();
		this.rnnoise?.disconnect();
		this.destination?.disconnect();
		this.processedTrack?.stop();

		if (this.audioContext && this.audioContext.state !== 'closed') {
			await this.audioContext.close();
		}

		this.audioContext = undefined;
		this.source = undefined;
		this.rnnoise = undefined;
		this.destination = undefined;
		this.processedTrack = undefined;
	}
}
